import {} from '@chakra-ui/react';
import {
  FieldPath,
  FieldValues,
  UseControllerProps,
  useController
} from 'react-hook-form';
import { MapperProps, withMapper } from '../../../utils/with-output-mapper';
import { Checkbox, CheckboxProps } from '../../atoms';
import { FormField, FormFieldProps } from './form-field';

type Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> = {
  label: string;
  multiple?: boolean;
} & MapperProps<TFieldValues, TName> &
  Omit<FormFieldProps, 'aria-invalid' | 'onChange'> &
  Omit<UseControllerProps<TFieldValues, TName>, 'defaultValue'> &
  Pick<CheckboxProps, 'isDisabled' | 'isChecked' | 'value'>;

export function FieldCheckbox<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  id,
  name,
  rules,
  isDisabled,
  defaultChecked,
  label,
  shouldUnregister,
  control,
  inputMapper,
  outputMapper,
  ...props
}: Props<TFieldValues, TName>): JSX.Element {
  const { field: regularField, fieldState } = useController({
    name,
    rules,
    shouldUnregister,
    control
  });

  const field = withMapper(regularField, inputMapper, outputMapper);

  return (
    <FormField
      error={fieldState.error}
      isInvalid={fieldState.invalid}
      id={id ?? `${field.name}-${field.value}`}
      name={field.name}
      isRequired={!!rules?.required}
      isDisabled={isDisabled}
    >
      <Checkbox
        id={id ?? `${field.name}-${field.value}`}
        isDisabled={isDisabled}
        defaultChecked={defaultChecked}
        onChange={field.onChange}
        {...props}
      >
        {label}
      </Checkbox>
    </FormField>
  );
}
